import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IndexedDBService } from '../indexeddb.service';
import {
  SaveSupplierSiteDataRequest,
  SaveSupplierSiteDataResponse,
  TagReplacement,
  GetSupplierSiteDataResponse
} from '../../interfaces/supplier-site-data.interface';

@Injectable({
  providedIn: 'root'
})
export class SupplierSiteDataService {
  private apiUrl = environment.companyGuidApiBaseUrl;

  constructor(
    private http: HttpClient,
    private indexedDBService: IndexedDBService
  ) {}

  async saveSupplierSiteData(companyGuid: string): Promise<SaveSupplierSiteDataResponse> {
    try {
      if (!companyGuid) {
        throw new Error('Company GUID is required');
      }

      const [dictContent, completeFormData, tagReplacements] = await Promise.all([
        this.indexedDBService.getDictContent(companyGuid),
        this.indexedDBService.getMetadata(`v2_completeFormData_${companyGuid}`),
        this.indexedDBService.getTagReplacements(companyGuid)
      ]);

      const requestPayload: SaveSupplierSiteDataRequest = {
        companyGuid: companyGuid,
        dictContent: dictContent ? JSON.stringify(dictContent) : '',
        completeFormData: Object.keys(completeFormData).length > 0 ? JSON.stringify(completeFormData) : '',
        tagReplacements: tagReplacements || []
      };

      const response = await firstValueFrom(
        this.http.post<SaveSupplierSiteDataResponse>(
          `${this.apiUrl}api/Supplier/SaveSupplierSiteData`,
          requestPayload
        )
      );
      return response ;

    } catch (error) {
      let errorMessage = 'Unknown error occurred while saving data';

      if (error instanceof Error) {
        if (error.message.includes('Network Error') || error.message.includes('Http failure')) {
          errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timeout: The server took too long to respond. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  saveSupplierSiteData$(companyGuid: string): Observable<SaveSupplierSiteDataResponse> {
    return from(this.saveSupplierSiteData(companyGuid));
  }

  async getSupplierSiteData(companyGuid: string): Promise<GetSupplierSiteDataResponse> {
    try {
      if (!companyGuid) {
        throw new Error('Company GUID is required');
      }

      const response = await firstValueFrom(
        this.http.get<GetSupplierSiteDataResponse>(
          `${this.apiUrl}api/Supplier/GetSupplierSiteData/${companyGuid}`
        )
      );
      return response;

    } catch (error) {
      let errorMessage = 'Unknown error occurred while retrieving data';

      if (error instanceof Error) {
        if (error.message.includes('Network Error') || error.message.includes('Http failure')) {
          errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timeout: The server took too long to respond. Please try again.';
        } else if (error.message.includes('404')) {
          errorMessage = 'No saved data found for this company.';
        } else {
          errorMessage = error.message;
        }
      }
      return {
        status: false,
        data: {
          success: false,
          data: {
            dictContent: '',
            tagReplacements: [],
            completeFormData: '',
            lastModified: ''
          },
          message: errorMessage
        },
        message: 'Request failed',
        errors: [errorMessage]
      };
    }
  }

  getSupplierSiteData$(companyGuid: string): Observable<GetSupplierSiteDataResponse> {
    return from(this.getSupplierSiteData(companyGuid));
  }
}
